/**
 * @ngdoc module
 *
 * @description
 *
 * Decorate Angular's built in `$log` service. More information in the config
 * file. Angular provides nice, cross-browser wrappers for built in console
 * services. In this file, each of these methods is decorated so that it prepends
 * the message with a timestamp. The service also keeps an internal record of all
 * messages, which is availabe on the `$log.messages` property. The service also
 * suppresses the messages from being displayed depending on the value of
 * `DebugSettings.LOG_LEVEL`.
 *
 * In the future, this could be extended to log messages to an API.
 *
 * @see buildium.common.LOG_LEVELS
 * @see buildium.common.DebugSettings
 */

export const LoggerConfig = function exports($provide, LOG_LEVELS, IS_DEBUGGING_ENABLED) {
    function LogMessage(error, timestamp, logLevel, args) {
        this.error = error;
        this.timestamp = timestamp;
        this.logLevel = logLevel;
        this.message = args;
    }

    const DebugSettings = {
        LOG_LEVEL: IS_DEBUGGING_ENABLED ? LOG_LEVELS.VERBOSE : LOG_LEVELS.NONE
    };

    // Only constants and providers can be injected into angular.config functions.
    // Because the `$log` service is decorated in a config block, there needs to be an
    // extra step taken to get the `DebugSettings` to it. In this case, it can access
    // the .settings` property on the provider. The underlying object is what will be
    // injected as the `DebugSettings` service for non-config blocks.
    const DebugSettingsProvider = $provide.provider('DebugSettings', {
        settings: DebugSettings,
        $get: function $get() {
            return this.settings;
        }
    });

    $provide.decorator('$log', ['$delegate', function decorator($delegate) {
        const messages = [],
            logFn = $delegate.log,
            debugFn = $delegate.debug,
            warnFn = $delegate.warn,
            errorFn = $delegate.error;

        Object.defineProperties($delegate, {
            messages: {
                get: function get() {
                    return messages;
                }
            }
        });

        /**
         * Generate functions that patch the originals so that
         *  1) they prepend a timestamp
         *  2) saves a reference to the message
         *  3) suppress output if debugging is disabled
         *
         * @param {number} logLevel - value from the LOG_LEVELS constant indicating at what levels messages should be shown or suppressed
         * @param {funciton} originalFn - the original function from Angular's `$log` to decorate
         */
        function createDecoratedFn(logLevel, originalFn) {
            return function monkeyPatch() {
                const stamp = new Date(),
                    // eslint-disable-next-line prefer-rest-params
                    args = Array.prototype.slice.call(arguments);

                // the Error object will give us a stack trace.
                messages.push(new LogMessage(
                    new Error(), stamp, logLevel, args
                ));

                // we have to access the provider directly, because values/services/etc aren't available in `angular.config` blocks
                if (logLevel >= DebugSettingsProvider.settings.LOG_LEVEL) {
                    originalFn.apply(this, [`${stamp}\n`].concat(args));
                }
            };
        }

        /**
         * Decorate a function so that a deprecation warning message is shown when it is invoked
         * @param {function} deprecatedFunction
         * @returns {function} the wrapped function
         */
        $delegate.deprecate = function deprecate(fn) {
            const fnString = fn.toString();

            return function deprecationDecoration() {
                // eslint-disable-next-line prefer-rest-params
                const args = Array.prototype.slice.call(arguments);

                $delegate.warn(`[DEPRECATED]:\n${fnString}`);

                return fn.apply(this, args);
            };
        };

        $delegate.log = createDecoratedFn(LOG_LEVELS.VERBOSE, logFn);
        $delegate.debug = createDecoratedFn(LOG_LEVELS.VERBOSE, debugFn);
        $delegate.warn = createDecoratedFn(LOG_LEVELS.WARN, warnFn);
        $delegate.error = createDecoratedFn(LOG_LEVELS.ERROR, errorFn);

        return $delegate;
    }]);
};

LoggerConfig.$inject = ['$provide', 'LOG_LEVELS', 'IS_DEBUGGING_ENABLED'];
